import { api } from "@/api/axios.ts";
import { ApiIdObject, ApiOperationResult } from "@/types/common.types.ts";
import {
  ApiTimeOffCategory,
  ApiTimeOffCategoryPayload,
  ApiTimeOffEmployeePolicyCheck,
  ApiTimeOffPolicy,
  ApiTimeOffPolicyAssignToPreview,
  ApiTimeOffPolicyAssignToPreviewPayload,
  ApiTimeOffPolicyPayload,
  ApiTimeOffPolicyTransition,
} from "@/types/time-off/time-off-policy.types.ts";
import {
  ApiEmployeeTimeOffCalendar,
  ApiTimeOffAdjustmentPayload,
  ApiTimeOffCalendarFilter,
  ApiTimeOffLog,
} from "@/types/time-off/time-off.types.ts";

export const apiTimeOffPolicy = {
  getAll: async function () {
    return api.get<ApiTimeOffCategory[]>("/api/v1/time-off/categories");
  },
  update: async function (id: number, payload: ApiTimeOffCategoryPayload) {
    return api.put<ApiTimeOffCategory>(`/api/v1/time-off/categories/${id}`, payload);
  },
  create: async function (payload: ApiTimeOffCategoryPayload) {
    return api.post<ApiTimeOffCategory>("/api/v1/time-off/categories", payload);
  },
  deleteCategory: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/time-off/categories/${id}`);
  },
  getCategoryById: async function (id: number) {
    return api.get<ApiTimeOffCategory>(`/api/v1/time-off/categories/${id}`);
  },
  getPolicyById: async function (id: number) {
    return api.get<ApiTimeOffPolicy>(`/api/v1/time-off/policies/${id}`);
  },
  createPolicy: async function (payload: ApiTimeOffPolicyPayload) {
    return api.post<ApiTimeOffPolicy>("/api/v1/time-off/policies", payload);
  },
  updatePolicy: async function (id: number, payload: ApiTimeOffPolicyPayload) {
    return api.put<ApiTimeOffPolicy>(`/api/v1/time-off/policies/${id}`, payload);
  },
  duplicatePolicy: async function (id: number) {
    return api.post<ApiIdObject>(`/api/v1/time-off/policies/${id}/duplicate`, {});
  },
  deletePolicy: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/time-off/policies/${id}`);
  },
  getEmployeePolicyChecks: async function (employeeId: number, categoryId: number) {
    return api.get<ApiTimeOffEmployeePolicyCheck[]>(
      `/api/v1/time-off/policies/employees/${employeeId}/categories/${categoryId}`
    );
  },
  createAdjustment: async function (payload: ApiTimeOffAdjustmentPayload) {
    return api.post<ApiOperationResult>("/api/v1/time-off/adjustment", payload);
  },
  getAdjustmentById: async function (id: number) {
    return api.get<ApiTimeOffLog>(`/api/v1/time-off/adjustment/${id}`);
  },
  updateAdjustment: async function (id: number, payload: ApiTimeOffAdjustmentPayload) {
    return api.put<ApiOperationResult>(`/api/v1/time-off/adjustment/${id}`, payload);
  },
  deleteAdjustment: async function (id: number) {
    return api.delete<ApiOperationResult>(`/api/v1/time-off/adjustment/${id}`);
  },
  calendar: async function (filter: ApiTimeOffCalendarFilter, signal?: AbortSignal) {
    return api.post<ApiEmployeeTimeOffCalendar[]>(`/api/v1/time-off/calendar`, filter, signal);
  },
  sort: async function (ids: number[]) {
    return api.put<ApiEmployeeTimeOffCalendar[]>(`/api/v1/time-off/categories/sort`, { ids });
  },
  sortPolicies: async function (categoryId: number, ids: number[]) {
    return api.put<ApiOperationResult>(`/api/v1/time-off/policies/sort`, {
      category_id: categoryId,
      ids,
    });
  },
  previewPoliciesSort: async function (categoryId: number, ids: number[]) {
    return api.post<ApiTimeOffPolicyTransition[]>(
      `/api/v1/time-off/categories/${categoryId}/policies/sort-preview`,
      { ids }
    );
  },
  previewPolicyAssignTo: async function (
    payload: ApiTimeOffPolicyAssignToPreviewPayload,
    signal?: AbortSignal
  ) {
    return api.post<ApiTimeOffPolicyAssignToPreview>(
      `/api/v1/time-off/policies/assign-to-preview`,
      payload,
      signal
    );
  },
};
